
import styled from '@emotion/styled';
import { BreakpointValue } from 'consts/breakpoints';
import { colorToAlpha } from 'helpers/insight';
import { WithContentSlotSidePaddingContainer } from 'ds/components/Layout';
import { breakpointToContentWidth } from 'ds/components/Layout';
import { WithInsightType } from './types';
import { headerHeightConsts } from 'ds/theme/default';
import { SwitchAction } from './HighlightsPagerControls';
import { InsightType } from 'utils/entities';
import { ProgressIndicatorProps } from './ProgressBar';
import { DESKTOP_HEADER_TOTAL_HEIGHT, HEADER_HEIGHT } from 'components/navigation/header.constants';
import { SmallText } from 'ds/components/typography';
import { LISTING_PAGE_CONTAINER_WIDTH } from 'screens/ListingPage/styled';

export const FeedbackSentWrapper = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.neutrals.grey3};
  svg {
    margin-${({ theme }) => theme.isRTL ? 'left' : 'right'}: 8px;
    path {
      fill: ${({ theme }) => theme.colors.neutrals.grey3};
    }
  }
`;

export const FeedbackDropdownWrapper = styled.div`
  position: relative;
  display: inline-block;
  cursor: pointer;
`;

export const FeedbackDropdownButtonWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.neutrals.grey3};
  svg:first-of-type {
    margin-${({ theme }) => theme.isRTL ? 'left' : 'right'}: 8px;
    path {
      fill: ${({ theme }) => theme.colors.neutrals.grey3};
    }
  }

  svg:last-of-type {
    transform: rotate(${({ theme }) => theme.isRTL ? '' : '-'}90deg);
    path {
      fill: ${({ theme }) => theme.colors.neutrals.grey3};
    }
  }

  &:hover {
    color: ${({ theme }) => theme.colors.neutrals.grey1};
    svg {
      path {
        fill: ${({ theme }) => theme.colors.neutrals.grey1};
      }
    }
  }
`;

export const HighlightWrapper = styled.div`
  width: 100%;
  position: relative;
`;

export const HIGHLIGHT_HEADER_HEIGHT = 84;

const HighlightHeaderBase = styled(WithContentSlotSidePaddingContainer)`
  z-index: 1;
  position: fixed;
  top: ${DESKTOP_HEADER_TOTAL_HEIGHT}px;
  right: 0;
  height: ${HIGHLIGHT_HEADER_HEIGHT}px;
  z-index: 2;
  padding: 24px 0;
  background: ${({ theme }) => theme.colors.neutrals.white};
  display: flex;
  align-items: center;

  ${({ theme }) => Object.keys(breakpointToContentWidth).map(key => {
    return theme.media.showAt({ at: (+key as BreakpointValue) }) `
      width: ${breakpointToContentWidth[key]};
    `;
  })}

  ${({ theme }) => theme.media.showAt({ from: 1, to: 2 }) `
    top: ${headerHeightConsts.mobile.autocomplete + headerHeightConsts.mobile.filters}px;
  `}
`;

export const ContainerNew = styled.div`
  min-height: calc(100vh - ${HEADER_HEIGHT + HIGHLIGHT_HEADER_HEIGHT}px);
`;
export const BackButtonContainer = styled.div(({ theme }) => `
  margin-left: ${theme.spacing(4)};
  & a {
    text-decoration: none;
    display: flex;
    gap: ${theme.spacing(1)};
    align-items: center;
    cursor: pointer;
  }
  & svg {
    transform: scaleX(-1);
  }
`);

export const BackButtonLinkText = styled(SmallText)`
  color: ${({ theme }) => theme.colors.neutrals.grey1};
`;

export const HighlightHeaderNew = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.neutrals.grey8};
  z-index: 1;
  position: sticky;
  top: ${HEADER_HEIGHT}px;
  right: 0;
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  height: ${HIGHLIGHT_HEADER_HEIGHT}px;
  z-index: 2;
  padding: ${({ theme }) => theme.spacing(3)} 0;
  background: ${({ theme }) => theme.colors.neutrals.white};
  display: flex;
  align-items: center;
  margin-right: calc(((100vw - (min(${LISTING_PAGE_CONTAINER_WIDTH}px, 100vw) - 120px)) / 2 - 32px) * -1);
  padding-right: calc(((100vw - (min(${LISTING_PAGE_CONTAINER_WIDTH}px, 100vw) - 120px)) / 2));
`;

export const HighlightHeader = styled(HighlightHeaderBase)<{ isScrolled: boolean }>`
  ${({ isScrolled, theme }) => isScrolled
    ? `border-bottom: 1px solid ${theme.colors.neutrals.grey9};`
    : 'border-bottom: 1px solid rgba(255, 255, 255, 0);'
  }
`;

export const HighlightHeaderTitle = styled.div<WithInsightType>`
  color: ${({ theme, insightType }) => theme.colors.insights[insightType]};
  & div {
    font-weight: 700;
    text-transform: capitalize;
  }
`;

export const HighlightHeaderTitleNew = styled(HighlightHeaderTitle)<WithInsightType>`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

export const HighlightTitle = styled.div<WithInsightType>`
  margin-bottom: 48px;
  color: ${({ theme, insightType }) => theme.colors.insights[insightType]};
`;

export const HighlightBody = styled.div`
  padding: 0 0 64px;
`;

export const HighlightFooter = styled.div``;

export const HighlightIconWrapper = styled.div<WithInsightType>`
  background: ${({ theme, insightType }) => colorToAlpha(theme.colors.insights[insightType], 0.12)};
  border-radius: ${({ theme }) => theme.borderRadius.round};
  height: 36px;
  width: 36px;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-${({ theme }) => theme.isRTL ? 'left' : 'right'}: 16px;
  svg {
    path {
      fill: ${({ theme, insightType }) => theme.colors.insights[insightType]};
    }
  }
`;


const FIXED_HEADER_HEIGHT = 48;

export const HighlightsMobileWrapper = styled.div`
  position: relative;
  overflow: hidden;
  &:before {
    position: fixed;
    content: '';
    top: -100%;
    bottom: -100%;
    left: 0;
    right: 0;
    background: ${({ theme }) => theme.colors.neutrals.white};
  }
`;

export const HighlightsMobileFooter = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: ${({ theme }) => colorToAlpha(theme.colors.neutrals.white, 0.9)};
  border-top: 1px solid ${({ theme }) => theme.colors.neutrals.grey8};
  padding-top: 12px;
  z-index: 1;
`;

export const MobileHighlightWrapper = styled.div`
  background-image: linear-gradient(to bottom, #fff, ${({ theme }) => theme.colors.neutrals.grey11});
  padding: 80px 0 24px;
  &:last-of-type {
    padding-bottom: 140px;
  }
`;

export const MobileHighlightTitle = styled.div<WithInsightType>`
  color: ${({ theme, insightType }) => theme.colors.insights[insightType]};
  margin: 16px auto 40px;
  text-align: center;
`;

export const MobileHighlightBody = styled.div`
  margin-bottom: 80px;
`;

export const GroupLabelHeader = styled.div`
  height: ${FIXED_HEADER_HEIGHT}px;
  top: 0;
  left: 0;
  right: 0;
  position: fixed;
  background: ${({ theme }) => theme.colors.neutrals.white};
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutrals.grey8};
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.colors.neutrals.grey1};
  z-index: 5;
`;

export const MobileHighlightIconWrapper = styled.div<WithInsightType>`
  background: ${({ theme, insightType }) => colorToAlpha(theme.colors.insights[insightType], 0.12)};
  border-radius: ${({ theme }) => theme.borderRadius.round};
  height: 36px;
  width: 36px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
  svg {
    path {
      fill: ${({ theme, insightType }) => theme.colors.insights[insightType]};
    }
  }
`;

export const MobileCloseButtonWrapper = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%) scaleX(${({ theme }) => theme.isRTL ? '-' : ''}1);
  ${({ theme }) => theme.isRTL ? 'right' : 'left'}: 24px;
  height: 24px;
  width: 24px;
  cursor: pointer;
`;


export const HighlightsPagerControlsInner = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-grow: 1;
`;

export const SwitchHighlightButtonWrapper = styled.div<{ action: SwitchAction, insightType: InsightType }>`
  height: 24px;
  width: 24px;
  position: absolute;
  ${({ theme, action }) => theme.isRTL
    ? (action === 'next' ? 'left' : 'right')
    : (action === 'next' ? 'right' : 'left')}: 24px;
  top: 50%;
  cursor: pointer;
  transform: translateY(-50%) scaleX(${({ theme, action }) => theme.isRTL
    ? (action === 'next' ? '' : '-')
    : (action === 'next' ? '-' : '')}1);
  transition: all .2s ease;
  svg {
    path {
      fill: ${({ theme }) => theme.colors.neutrals.grey1};
    }
  }
  &:hover {
    svg {
      path {
        fill: ${({ theme, insightType }) => theme.colors.insights[insightType]};
      }
    }
  }
`;

export const SwitchHighlightPreviewWrapper = styled.div<{ insightType: InsightType, action: SwitchAction }>`
  color: ${({ theme, insightType }) => theme.colors.insights[insightType]};
  display: flex;
  align-items: center;
  position: absolute;
  transform: translateY(-50%);
  top: 50%;
  padding: 0 16px;
  ${({ theme, action }) => theme.isRTL
    ? (action === 'next' ? 'left' : 'right')
    : (action === 'next' ? 'right' : 'left')}: 72px;
  flex-direction: row${({ action }) => action === 'next' ? '' : '-reverse'};
  &:after {
    content: '';
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    height: 24px;
    width: 1px;
    background: ${({ theme }) => theme.colors.neutrals.grey9};
    ${({ theme, action }) => theme.isRTL
      ? (action === 'next' ? 'left' : 'right')
      : (action === 'next' ? 'right' : 'left')}: 0;
  }
`;

export const SwitchHighlightPreviewIcon = styled.div<{ insightType: InsightType, action: SwitchAction }>`
  background: ${({ theme, insightType }) => colorToAlpha(theme.colors.insights[insightType], 0.12)};
  border-radius: ${({ theme }) => theme.borderRadius.round};
  height: 32px;
  width: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-${({ theme, action }) => theme.isRTL
    ? (action === 'next' ? 'left' : 'right')
    : (action === 'next' ? 'right' : 'left')}: 16px;
  svg {
    path {
      fill: ${({ theme, insightType }) => theme.colors.insights[insightType]};
    }
  }
`;

export const SwitchHighlightCounter = styled.div`
  & > div {
    color: ${({ theme }) => theme.colors.neutrals.grey1};
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

export const ProgressIndicator = styled.div<ProgressIndicatorProps>`
  width: 6px;
  height: 6px;
  background: ${({ theme }) => theme.colors.neutrals.grey6};
  border-radius: ${({ theme }) => theme.borderRadius.round};
  margin: 0 4px;
  transition: all .2s linear;
  position: relative;
  overflow: hidden;
  ${({ isActive, theme, progress, insightType }) => isActive ? `
    width: 12px;
    border-radius: ${theme.borderRadius.smallestSideRound};
    &:after {
      content: '';
      ${theme.isRTL ? 'right' : 'left'}: 0;
      position: absolute;
      height: 100%;
      width: ${progress * 100}%;
      background: ${theme.colors.insights[insightType]};
    }
  ` : ``}
`;

export const ProgressWrapper = styled.div`
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => colorToAlpha(theme.colors.neutrals.white, .9)};
`;
